import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-modal";
import { toast } from "react-toastify";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import CloseSubmitBtn from "../common-components/custom-comps/CloseSubmitBtn";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { useGetFetchNew } from "../../data/Queries";
import { get_devices, update_device_id } from "../../data/ApiEndpoints";
import { showConfirmAlert } from "../components-react/ReactAlert";

const DeviceIdForm = ({
  isOpen,
  onClose,
  selectedGymData,
  userInfoData,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const {
    data: gymDevices,
    isLoading,
    refetch,
    isFetching,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "gym_devices",
    url: `${get_devices}?gym_uid=${selectedGymData?.user_id}`,
    filters: {},
  });

  useEffect(() => {
    refetch();
  }, [selectedGymData]);

  const { postPutLoading } = useSelector((state) => state.common);

  const [deviceIds, setDeviceIds] = useState("");
  const [originalDeviceIds, setOriginalDeviceIds] = useState("");

  // Device ID format validation
  const validateDeviceId = (deviceId) => {
    const trimmedId = deviceId.trim().toUpperCase();

    // Check if it's a valid MAC-like format (12 hex characters)
    const macRegex = /^[0-9A-F]{12}$/;
    const macWithColonRegex = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;
    const macWithDashRegex = /^([0-9A-F]{2}-){5}[0-9A-F]{2}$/;

    if (macRegex.test(trimmedId)) {
      return { isValid: true, formatted: trimmedId };
    }

    if (macWithColonRegex.test(trimmedId) || macWithDashRegex.test(trimmedId)) {
      const formatted = trimmedId.replace(/[:-]/g, "");
      return { isValid: true, formatted };
    }

    return { isValid: false, formatted: trimmedId };
  };

  // const validateAllDeviceIds = (inputString) => {
  //   const deviceIdArray = inputString
  //     .split(",")
  //     .map((id) => id.trim())
  //     .filter((id) => id.length > 0);

  //   const errors = [];
  //   const validIds = [];

  //   deviceIdArray.forEach((id, index) => {
  //     const validation = validateDeviceId(id);
  //     if (!validation.isValid) {
  //       errors.push(
  //         `Device ID ${
  //           index + 1
  //         } (${id}): Invalid format. Expected 12 hex characters`
  //       );
  //     } else {
  //       validIds.push(validation.formatted);
  //     }
  //   });

  //   // Check for duplicates
  //   const duplicates = validIds.filter(
  //     (id, index) => validIds.indexOf(id) !== index
  //   );
  //   if (duplicates.length > 0) {
  //     errors.push(
  //       `Duplicate device IDs found: ${[...new Set(duplicates)].join(", ")}`
  //     );
  //   }

  //   return { errors, validIds: [...new Set(validIds)] };
  // };

  const getChangesSummary = () => {
    const currentIds = deviceIds
      .split(",")
      .map((id) => id.trim().toUpperCase())
      .filter((id) => id.length > 0);

    const originalIds = originalDeviceIds
      .split(",")
      .map((id) => id.trim().toUpperCase())
      .filter((id) => id.length > 0);

    const added = currentIds.filter((id) => !originalIds.includes(id));
    const removed = originalIds.filter((id) => !currentIds.includes(id));

    return {
      added,
      removed,
      hasChanges: added.length > 0 || removed.length > 0,
    };
  };

  const performDeviceUpdate = async () => {
    try {
      // Clean and split user input device IDs, remove empty ones
      const userInputDeviceIds = deviceIds
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      console.log("userInputDeviceIds", userInputDeviceIds);

      const payload = {
        device_id: userInputDeviceIds,
        gym_uid: selectedGymData?.user_id,
        status: "active",
      };

      const result = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: update_device_id,
          payload,
          token: userInfoData?.token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message || "Device IDs updated successfully!"
        );
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        const errorMessage =
          result?.payload?.data ||
          result?.payload?.message ||
          "Failed to update device IDs";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating device IDs:", error);
      toast.error("Failed to update device IDs");
    }
  };

  const handleUpdateDeviceId = async () => {
    if (!deviceIds.trim()) {
      toast.error("Please enter device ID");
      return;
    }

    if (!selectedGymData?.gym_id) {
      toast.error("Gym information is missing");
      return;
    }

    const changes = getChangesSummary();

    if (!changes.hasChanges) {
      toast.info("No changes detected in device IDs");
      return;
    }

    // Create confirmation message
    let confirmMessage = `⚠️ You are about to update device IDs for "${
      selectedGymData?.gym_name || "Unknown Gym"
    }"\n\n`;

    if (changes.added.length > 0) {
      confirmMessage += `✅ Adding ${
        changes.added.length
      } device(s):\n${changes.added.join(", ")}\n\n`;
    }

    if (changes.removed.length > 0) {
      confirmMessage += `❌ Removing ${
        changes.removed.length
      } device(s):\n${changes.removed.join(", ")}\n\n`;
    }

    confirmMessage +=
      "This action may affect device connectivity. Are you sure you want to continue?";

    showConfirmAlert(performDeviceUpdate, confirmMessage);
  };

  const handleClose = () => {
    setDeviceIds("");
    setOriginalDeviceIds("");
    onClose();
  };

  useEffect(() => {
    if (isOpen && selectedGymData && gymDevices?.data) {
      const deviceIdValues = gymDevices?.data
        .map((device) => device.device_id)
        .filter((deviceId) => deviceId)
        .join(", ");

      setDeviceIds(deviceIdValues);
      setOriginalDeviceIds(deviceIdValues);
    }
  }, [selectedGymData, isOpen, gymDevices]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Device ID Modal"
      ariaHideApp={false}
    >
      <div className="createNewCTOModalContainer mod-width-50p">
        <ModalHeadAndClose
          title={`Update Device ID - ${selectedGymData?.gym_name || "Gym"}`}
          onClose={handleClose}
        />

        <div className="form-container">
          <div className="formContainer">
            <div className="formInputBox">
              <label htmlFor="deviceIds" className="formInputLabel">
                Device ID <span className="required">*</span>
              </label>
              <textarea
                id="deviceIds"
                placeholder="Enter device IDs separated by commas (e.g. E03C1CB1DC8BAA01, E93C1CB1DC8BAA01)"
                className="formInput"
                value={deviceIds}
                onChange={(e) => setDeviceIds(e.target.value)}
                rows="3"
              />
              <small className="text-muted mt-1 d-block">
                Enter device IDs separated by commas.
                <br />
                ⚠️ <strong>Warning:</strong> Changes will affect device
                connectivity. Double-check before updating.
              </small>
            </div>

            <CloseSubmitBtn
              closeLabel="Cancel"
              submitLabel="Update Device ID"
              onClose={handleClose}
              onSubmit={handleUpdateDeviceId}
              isLoading={postPutLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeviceIdForm;
